// This is a client-side wrapper for making API calls to our server
// for sending emails via Resend
import { supabase } from '@/lib/supabase/client';

interface SendEmailParams {
  to: string;
  subject: string;
  templateName: string;
  templateData: Record<string, unknown>;
}

export async function sendEmail({ to, subject, templateName, templateData }: SendEmailParams) {
  try {
    // Get the Supabase email function URL from environment variables
    const emailFunctionUrl = import.meta.env.VITE_SUPABASE_EMAIL_FUNCTION_URL as string;
    
    if (!emailFunctionUrl) {
      throw new Error('Email function URL not found in environment variables');
    }
    
    console.log(`Using email function URL: ${emailFunctionUrl}`);
    
    // Get the current session
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    
    if (!token) {
      throw new Error('No authentication token available. Please sign in.');
    }
    
    // Try using XMLHttpRequest instead of fetch as an alternative
    // Sometimes this works better with CORS in certain environments
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', emailFunctionUrl, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Accept', 'application/json');
      
      // Set withCredentials to false for CORS
      xhr.withCredentials = false;
      
      xhr.onload = function() {
        if (this.status >= 200 && this.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_) {
            reject(new Error('Invalid response from server'));
          }
        } else {
          let errorMsg = 'Server error';
          try {
            const errorData = JSON.parse(xhr.responseText);
            errorMsg = errorData.error || errorMsg;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_) {
            // Use default error message
          }
          reject(new Error(`Failed to send email: ${errorMsg}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error occurred'));
      };
      
      xhr.send(JSON.stringify({
        to,
        subject,
        templateName,
        templateData,
      }));
    });
  } catch (error) {
    console.error('Failed to send email:', error);
    throw error;
  }
} 